import { useEffect, useState } from "react";
import { useNavigate, useSearch } from "@tanstack/react-router";
import { UsersThree } from "@phosphor-icons/react";
import { Button, Checkbox, EmptyState, ExpandedRow, Field, Lookup, PageHeader, Table, TBody, TD, TDNum, TH, THead, TR, formatCount } from "@ghezaa/ui";
import { useDebounced } from "@ghezaa/shell";
import type { PersonRef, SalespersonRow } from "@/api/client";
import { useLinkPerson } from "@/api/mutations";
import { usePeople, usePeopleSearch } from "@/api/queries";
import { useCurrentMarket } from "@/lib/market";

export type PeopleSearch = { open?: string };

export function peopleSearch(search: Record<string, unknown>): PeopleSearch {
  return typeof search.open === "string" && search.open !== "" ? { open: search.open } : {};
}

/**
 * Who each BC salesperson code is. BC knows codes, not people: a person's own book is the customers whose code is linked
 * to them here. A row opens to link it, name the manager, or mark the code as no one.
 */
export function PeoplePage() {
  const market = useCurrentMarket();
  const people = usePeople(market.code);
  const search = useSearch({ strict: false }) as PeopleSearch;
  const navigate = useNavigate();
  const open = (code: string | undefined) => void navigate({ to: ".", search: { open: code } as never, replace: true });
  const rows = people.data ?? [];
  const unlinked = rows.filter((r) => !r.person && !r.notAPerson && r.inBc).length;
  const description =
    people.data && rows.length > 0
      ? `${formatCount(rows.length)} salesperson codes in ${market.name}${unlinked ? ` · ${formatCount(unlinked)} not linked yet` : ""}.`
      : undefined;

  return (
    <div className="flex flex-col gap-6">
      <PageHeader title="People" description={description} />
      {people.isError && <p className="bg-danger-soft px-3 py-2 text-sm text-danger">Could not load the salesperson codes: {people.error.message}</p>}
      {people.data && rows.length === 0 ? (
        <EmptyState icon={UsersThree} title="No salesperson codes yet" description="They come from Business Central: link the market to its company first, on the Business Central page." />
      ) : (
        people.data && (
          <Table>
            <THead>
              <TR>
                <TH>Code</TH>
                <TH from="md" className="text-end">Customers</TH>
                <TH>Person</TH>
                <TH from="lg" className="whitespace-nowrap">Reports to</TH>
              </TR>
            </THead>
            <TBody>
              {rows.map((r) => {
                const selected = search.open === r.code;
                return [
                  <TR key={r.code} data-state={selected ? "selected" : undefined}>
                    <TD className="w-full max-w-0">
                      <button type="button" onClick={() => open(selected ? undefined : r.code)} aria-expanded={selected} className="truncate text-start underline-offset-2 hover:underline">
                        <span className="font-mono text-xs">{r.code}</span>
                      </button>
                      <span className="block truncate text-xs text-ink-3">
                        {[r.name, r.jobTitle].filter(Boolean).join(" · ") || (r.inBc ? "" : "No longer in Business Central")}
                      </span>
                    </TD>
                    <TDNum from="md">{r.customers || ""}</TDNum>
                    <TD className="whitespace-nowrap">
                      <PersonCell row={r} />
                    </TD>
                    <TD from="lg" className="whitespace-nowrap">{r.manager?.name ?? ""}</TD>
                  </TR>,
                  selected && (
                    <ExpandedRow key={`${r.code}-open`} colSpan={4}>
                      <PersonEditor row={r} onDone={() => open(undefined)} />
                    </ExpandedRow>
                  ),
                ];
              })}
            </TBody>
          </Table>
        )
      )}
    </div>
  );
}

function PersonCell({ row }: { row: SalespersonRow }) {
  if (row.notAPerson) return <span className="text-ink-3">No one</span>;
  if (row.person) {
    return (
      <span className="flex flex-col">
        <span>{row.person.name}</span>
        <span className="text-xs text-ink-3">{row.person.email}</span>
      </span>
    );
  }
  return <span className="text-ink-3">{row.suggested ? `Not linked · suggested ${row.suggested.name}` : "Not linked"}</span>;
}

function PersonLookup({ id, value, onChange, placeholder, disabled }: { id: string; value: PersonRef | null; onChange: (p: PersonRef | null) => void; placeholder: string; disabled?: boolean }) {
  const [query, setQuery] = useState("");
  const needle = useDebounced(query.trim());
  const matches = usePeopleSearch(needle);
  if (disabled) return <p className="text-sm text-ink-3">{value?.name ?? "No one"}</p>;
  return (
    <Lookup
      id={id}
      query={query}
      onQueryChange={setQuery}
      options={needle.length >= 2 ? matches.data : undefined}
      optionKey={(p) => p.id}
      renderOption={(p) => (
        <span className="flex flex-col">
          <span>{p.name}</span>
          <span className="text-xs text-ink-3">{p.email}</span>
        </span>
      )}
      onPick={(p) => {
        setQuery("");
        onChange(p);
      }}
      picked={value ? `${value.name} · ${value.email || "manager"}` : undefined}
      onChange={() => onChange(null)}
      loading={matches.isFetching}
      placeholder={placeholder}
      empty={matches.isError ? "Core cannot be reached right now." : "Nobody in Core matches."}
    />
  );
}

/** The open row: who the code is, their manager, or no one. Escape or Cancel closes it. */
function PersonEditor({ row, onDone }: { row: SalespersonRow; onDone: () => void }) {
  const market = useCurrentMarket();
  const save = useLinkPerson(market.code);
  const [person, setPerson] = useState<PersonRef | null>(row.person ?? row.suggested);
  const [manager, setManager] = useState<PersonRef | null>(row.manager);
  const [notAPerson, setNotAPerson] = useState(row.notAPerson);

  useEffect(() => {
    const close = (e: KeyboardEvent) => e.key === "Escape" && onDone();
    window.addEventListener("keydown", close);
    return () => window.removeEventListener("keydown", close);
  }, [onDone]);

  const submit = (body: { userId: string | null; managerUserId: string | null; notAPerson: boolean }) =>
    save.mutate({ code: row.code, ...body }, { onSuccess: onDone });

  return (
    <form
      className="flex max-w-[720px] flex-col gap-4 py-2"
      onSubmit={(e) => {
        e.preventDefault();
        submit({ userId: notAPerson ? null : (person?.id ?? null), managerUserId: notAPerson || !person ? null : (manager?.id ?? null), notAPerson });
      }}
    >
      <div className="grid gap-4 md:grid-cols-2">
        <Field label={`Who ${row.code} is`} htmlFor={`person-${row.code}`} hint={row.suggested && !row.person ? `${row.suggested.name} has the address BC gives this code.` : undefined}>
          <PersonLookup id={`person-${row.code}`} value={person} onChange={setPerson} placeholder="Search people in Core" disabled={notAPerson} />
        </Field>
        <Field label="Reports to" htmlFor={`manager-${row.code}`}>
          <PersonLookup id={`manager-${row.code}`} value={manager} onChange={setManager} placeholder="Search people in Core" disabled={notAPerson || !person} />
        </Field>
      </div>
      <Checkbox label="No one: a shared code, or one kept for old invoices" checked={notAPerson} onChange={(e) => setNotAPerson(e.target.checked)} />
      {save.isError && <p className="text-xs text-danger">{save.error.message}</p>}
      <div className="flex flex-wrap items-center gap-2">
        <Button type="submit" variant="primary" disabled={save.isPending || (!notAPerson && !person && !row.person && !row.notAPerson)}>
          Save
        </Button>
        <Button type="button" onClick={onDone}>
          Cancel
        </Button>
        {(row.person || row.notAPerson) && (
          <Button type="button" variant="ghost" disabled={save.isPending} onClick={() => submit({ userId: null, managerUserId: null, notAPerson: false })}>
            Unlink
          </Button>
        )}
      </div>
    </form>
  );
}
